import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Instagram, Youtube } from "lucide-react";
import { TikTokIcon, PinterestIcon, ThreadsIcon } from "@/lib/bioIcons";

// Single source of truth for site-wide social links: the CMS "Contatti" settings.
// Used by both the Footer and the Homepage social section (no duplicated data).
const DEFS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "pinterest", label: "Pinterest", Icon: PinterestIcon },
  { key: "threads", label: "Threads", Icon: ThreadsIcon },
];

export function useSocials() {
  const [data, setData] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/settings/contatti").then((r) => r.data?.data || {}).catch(() => ({})),
      api.get("/settings/footer").then((r) => r.data?.data || {}).catch(() => ({})),
    ]).then(([contatti, footer]) => setData({ contatti, footer }));
  }, []);
  if (!data) return [];
  // Primary source: per-platform fields in "Contatti".
  const fromContatti = DEFS
    .map((d) => ({ ...d, url: (data.contatti[d.key] || "").trim() }))
    .filter((s) => s.url);
  if (fromContatti.length) return fromContatti;
  // Backward-compatible fallback: the legacy Footer socials list [{label, link}].
  const byLabel = Object.fromEntries(DEFS.map((d) => [d.label.toLowerCase(), d]));
  return (data.footer.socials || [])
    .map((s) => {
      const def = byLabel[(s.label || "").toLowerCase()];
      const url = (s.link || s.url || "").trim();
      return def && url ? { ...def, url } : null;
    })
    .filter(Boolean);
}

// Prominent, homepage-integrated social section. Auto-hides when nothing configured.
export function HomeSocialSection() {
  const socials = useSocials();
  if (!socials.length) return null;
  return (
    <section className="bg-vs-bg py-16 md:py-24" data-testid="home-social-section" aria-labelledby="home-social-title">
      <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
        <span className="text-vs-orange font-bold uppercase tracking-[0.18em] text-sm">Restiamo in contatto</span>
        <h2 id="home-social-title" className="font-serif text-4xl md:text-5xl font-extrabold mt-3 tracking-tight leading-[1.08]">Seguimi sui social</h2>
        <p className="text-vs-text/70 mt-5 text-lg max-w-xl mx-auto">Dietro le quinte dei viaggi, consigli lampo e nuove risorse appena escono: ci vediamo lì.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3 md:gap-4">
          {socials.map(({ key, label, url, Icon }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              data-testid={`home-social-${key}`}
              className="group inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-3.5 shadow-vs-soft hover:shadow-vs-lift hover:-translate-y-1 transition-all duration-300"
            >
              <span className="w-10 h-10 rounded-xl bg-vs-mint flex items-center justify-center text-vs-green group-hover:bg-vs-green group-hover:text-white transition-colors">
                <Icon className="w-5 h-5" />
              </span>
              <span className="font-bold text-vs-text">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
